import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  PropsWithChildren,
} from 'react';
import { useSupabaseAuth } from '@/lib/supabase';
import { DeviceIdentifier } from './DeviceIdentifier';
import { ScopedAsyncStorage } from './ScopedAsyncStorage';

/**
 * Storage scope information
 */
export interface StorageScope {
  userId: string | null;
  deviceId: string;
  isReady: boolean;
}

/**
 * Context value for user device storage
 */
interface UserDeviceContextValue {
  /** Scoped storage instance */
  storage: ScopedAsyncStorage | null;
  /** Current storage scope info */
  scope: StorageScope;
  /** Whether the storage system is ready to use */
  isReady: boolean;
}

const UserDeviceContext = createContext<UserDeviceContextValue | undefined>(undefined);

/**
 * UserDeviceProvider
 * Provides scoped storage context based on current user and device.
 * Must be wrapped inside SupabaseProvider.
 */
export function UserDeviceProvider({ children }: PropsWithChildren) {
  const { user, loading: authLoading } = useSupabaseAuth();
  const [deviceId, setDeviceId] = useState<string | null>(null);
  const [deviceIdLoading, setDeviceIdLoading] = useState(true);

  // Initialize device ID
  useEffect(() => {
    const initDeviceId = async () => {
      try {
        const deviceIdentifier = DeviceIdentifier.getInstance();
        const id = await deviceIdentifier.getDeviceId();
        setDeviceId(id);
      } catch (error) {
        console.error('[UserDeviceProvider] Failed to get device ID:', error);
        // Use a session-scoped fallback
        setDeviceId(`session_${Date.now()}`);
      } finally {
        setDeviceIdLoading(false);
      }
    };

    initDeviceId();
  }, []);

  // Create scoped storage instance
  const storage = useMemo(() => {
    if (!deviceId) {
      return null;
    }
    return new ScopedAsyncStorage(user?.id ?? null, deviceId);
  }, [user?.id, deviceId]);

  // Build scope info
  const scope = useMemo<StorageScope>(() => ({
    userId: user?.id ?? null,
    deviceId: deviceId ?? '',
    isReady: !authLoading && !deviceIdLoading && deviceId !== null,
  }), [user?.id, deviceId, authLoading, deviceIdLoading]);

  const isReady = !authLoading && !deviceIdLoading && deviceId !== null;

  const value = useMemo<UserDeviceContextValue>(() => ({
    storage,
    scope,
    isReady,
  }), [storage, scope, isReady]);

  return (
    <UserDeviceContext.Provider value={value}>
      {children}
    </UserDeviceContext.Provider>
  );
}

/**
 * Hook to access the scoped storage instance
 * @throws Error if used outside UserDeviceProvider
 */
export function useScopedStorage(): ScopedAsyncStorage | null {
  const context = useContext(UserDeviceContext);
  if (context === undefined) {
    throw new Error('useScopedStorage must be used within a UserDeviceProvider');
  }
  return context.storage;
}

/**
 * Hook to check if scoped storage is ready
 */
export function useScopedStorageReady(): boolean {
  const context = useContext(UserDeviceContext);
  if (context === undefined) {
    throw new Error('useScopedStorageReady must be used within a UserDeviceProvider');
  }
  return context.isReady;
}

/**
 * Hook to access storage scope information
 */
export function useStorageScope(): StorageScope {
  const context = useContext(UserDeviceContext);
  if (context === undefined) {
    throw new Error('useStorageScope must be used within a UserDeviceProvider');
  }
  return context.scope;
}

/**
 * Hook that provides storage only when ready
 * Returns null while loading, throws if storage initialization fails after ready
 */
export function useRequiredScopedStorage(): ScopedAsyncStorage | null {
  const context = useContext(UserDeviceContext);
  if (context === undefined) {
    throw new Error('useRequiredScopedStorage must be used within a UserDeviceProvider');
  }
  
  if (!context.isReady) {
    return null;
  }
  
  if (!context.storage) {
    throw new Error('Storage is ready but instance is null - this should not happen');
  }
  
  return context.storage;
}
